export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  filename: string,
  columns?: { key: keyof T; label: string }[]
) {
  if (data.length === 0) {
    alert('No data to export');
    return;
  }

  // If no columns specified, use all keys from first object
  const columnsToExport = columns || Object.keys(data[0]).map(key => ({
    key: key as keyof T,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
  }));

  // Create CSV header
  const header = columnsToExport.map(col => col.label).join(',');

  // Create CSV rows
  const rows = data.map(row => {
    return columnsToExport
      .map(col => {
        const value = row[col.key];

        // Handle different value types
        if (value === null || value === undefined) {
          return '';
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return `"${value.join('; ')}"`;
        }

        // Handle objects
        if (typeof value === 'object') {
          return `"${JSON.stringify(value)}"`;
        }

        // Handle strings with commas or quotes
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      })
      .join(',');
  });

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function exportTableToCSV(
  tableId: string,
  filename: string
) {
  const table = document.getElementById(tableId) as HTMLTableElement;
  if (!table) {
    console.error(`Table with id "${tableId}" not found`);
    return;
  }

  const rows: string[][] = [];

  // Get headers
  const headerCells = table.querySelectorAll('thead th');
  const headers = Array.from(headerCells).map(cell => cell.textContent || '');
  rows.push(headers);

  // Get body rows
  const bodyRows = table.querySelectorAll('tbody tr');
  bodyRows.forEach(row => {
    const cells = row.querySelectorAll('td');
    const rowData = Array.from(cells).map(cell => {
      const text = cell.textContent || '';
      // Escape commas and quotes
      if (text.includes(',') || text.includes('"')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    });
    rows.push(rowData);
  });

  // Create CSV
  const csv = rows.map(row => row.join(',')).join('\n');

  // Download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
